import { NavLink, useNavigate, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { ChartBar, Users, SignOut, Lightning, ShieldCheck, Buildings, ChartLineUp, Bell, Trophy } from "@phosphor-icons/react";

const BASE_NAV = [
  { to: "/dashboard", label: "Dashboard", icon: ChartBar, testId: "nav-dashboard" },
  { to: "/candidates", label: "Candidates", icon: Users, testId: "nav-candidates" },
  { to: "/clients", label: "Clients", icon: Buildings, testId: "nav-clients" },
  { to: "/placements", label: "Placements", icon: Trophy, testId: "nav-placements" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [actionCount, setActionCount] = useState(0);

  // Poll today-actions count every 60s so the bell badge stays fresh
  useEffect(() => {
    if (!user) return undefined;
    let alive = true;
    const fetchCount = () => {
      api.get("/dashboard/today-actions")
        .then((r) => { if (alive) setActionCount(r.data.total || 0); })
        .catch(() => {});
    };
    fetchCount();
    const iv = setInterval(fetchCount, 60000);
    return () => { alive = false; clearInterval(iv); };
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const NAV = user?.role === "admin"
    ? [
        ...BASE_NAV,
        { to: "/insights", label: "Insights", icon: ChartLineUp, testId: "nav-insights" },
        { to: "/team", label: "Team", icon: ShieldCheck, testId: "nav-team" },
      ]
    : BASE_NAV;

  return (
    <div className="min-h-screen bg-[#FAFAFA] font-body text-zinc-950">
      <header
        className="sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-zinc-200"
        data-testid="app-header"
      >
        <div className="max-w-[1500px] mx-auto px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-12">
            <div className="flex items-center gap-2" data-testid="logo">
              <Lightning weight="fill" size={20} className="text-[#FF2A2A]" />
              <span className="font-heading font-black text-lg tracking-tight">
                RECRUITFLOW
              </span>
            </div>
            <nav className="flex items-center gap-6">
              {NAV.map(({ to, label, icon: Icon, testId }) => (
                <NavLink
                  key={to}
                  to={to}
                  data-testid={testId}
                  className={({ isActive }) =>
                    `flex items-center gap-1.5 text-sm font-medium transition-colors ${
                      isActive ? "text-zinc-950" : "text-zinc-500 hover:text-zinc-950"
                    }`
                  }
                >
                  <Icon size={16} weight="bold" />
                  {label}
                </NavLink>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-4">
            <Link
              to="/dashboard"
              data-testid="notifications-bell"
              className="relative text-zinc-500 hover:text-zinc-950 transition-colors"
              title={actionCount ? `${actionCount} action${actionCount === 1 ? "" : "s"} today` : "Today's actions"}
            >
              <Bell size={18} weight="bold" />
              {actionCount > 0 && (
                <span
                  data-testid="notifications-badge"
                  className="absolute -top-1.5 -right-2 min-w-[16px] h-[16px] px-1 rounded-full bg-[#FF2A2A] text-white text-[10px] font-bold flex items-center justify-center leading-none"
                >
                  {actionCount > 99 ? "99+" : actionCount}
                </span>
              )}
            </Link>
            <Link
              to="/settings"
              data-testid="settings-link"
              className="flex items-center gap-3 hover:opacity-80 transition-opacity"
              title="Account settings"
            >
              <div className="text-right hidden md:block">
                <div className="text-sm font-medium leading-tight" data-testid="user-name">
                  {user?.name}
                </div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                  {user?.email}
                </div>
              </div>
              <div className="w-9 h-9 rounded-sm bg-zinc-950 text-white flex items-center justify-center font-heading font-bold text-sm">
                {user?.name?.charAt(0)?.toUpperCase() || "U"}
              </div>
            </Link>
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-950 transition-colors"
            >
              <SignOut size={16} weight="bold" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1500px] mx-auto px-8 py-10">{children}</main>
    </div>
  );
}
